import type { EntityState, PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import {
  updateGreeting,
  updatePersonality,
  updateRole,
  updateRules,
} from '@polyai/stream/store/reducers/agent-settings/actions';
import { updateChannelGreeting } from '@polyai/stream/store/reducers/channels/actions';
import {
  createFlowStep,
  createFlowTransitionFunction,
  createNoCodeStep,
  deleteFlowStep,
  deleteFlowTransitionFunction,
  deleteNoCodeStep,
  deleteStep,
  importFlow,
  updateFlowStep,
  updateFlowTransitionFunction,
  updateNoCodeStep,
} from '@polyai/stream/store/reducers/flows/actions';
import {
  createFunction,
  deleteFunction,
  updateFunction,
  updateLatencyControl,
} from '@polyai/stream/store/reducers/functions/actions';
import {
  createTopic,
  deleteTopic,
  importTopics,
  updateTopic,
} from '@polyai/stream/store/reducers/knowledge-base/actions';
import {
  createTemplate,
  deleteTemplate,
  updateTemplate,
} from '@polyai/stream/store/reducers/sms/actions';
import {
  createEndFunction,
  createStartFunction,
  deleteEndFunction,
  deleteStartFunction,
  updateEndFunction,
  updateStartFunction,
} from '@polyai/stream/store/reducers/specialFunctions';
import {
  getChannelGreetingReferenceKey,
  variablesReferencesAdapter,
} from '@polyai/stream/store/reducers/variables/utils';
import type { References } from '@polyai/stream/store/references';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';
import {
  BEHAVIOUR_REFERENCE_KEY,
  GREETING_REFERENCE_KEY,
  handleDeleteEntityReferences,
  handleUpdateEntityReferences,
  PERSONALITY_REFERENCE_KEY,
  ROLE_REFERENCE_KEY,
  updateSingletonReferences,
} from '@polyai/stream/store/utils/reference-helpers';

import { createVariable, deleteVariable, updateVariable } from './actions';
import type { Variable, VariableReference } from './types';

const variablesAdapter = createEntityAdapter<Variable>();

const variablesSelectors = variablesAdapter.getSelectors();

// Wrapper: remove a reference and delete the variable if it has no remaining references
const removeReferenceAndPrune = (
  entityState: EntityState<
    { references?: References<VariableReference> },
    string
  >,
  parentId: string,
  ref: VariableReference,
  id: string,
) => {
  variablesReferencesAdapter.removeReference(entityState, parentId, ref, id);

  const typedState = entityState as unknown as EntityState<Variable, string>;
  const v = typedState.entities[parentId] as Variable | undefined;
  if (!v) {
    return;
  }
  const hasAnyRefs = Object.values(v.references ?? {}).some(
    group => Object.keys(group ?? {}).length > 0,
  );
  if (!hasAnyRefs) {
    variablesAdapter.removeOne(typedState, parentId);
  }
};

type VariableRefPayload = {
  id: string;
  references?: { variables?: Record<string, boolean> };
};

const handleCreate = (
  state: { variables: EntityState<Variable, string> },
  action: PayloadAction<VariableRefPayload>,
  refKey: VariableReference,
) => {
  const id = action.payload.id;
  const variableRefs = action.payload.references?.variables;
  if (variableRefs === undefined) {
    return;
  }
  handleUpdateEntityReferences(
    state.variables.entities,
    id,
    variableRefs,
    variablesReferencesAdapter.addReference,
    removeReferenceAndPrune,
    state.variables,
    refKey,
  );
};

const handleUpdate = (
  state: { variables: EntityState<Variable, string> },
  action: PayloadAction<VariableRefPayload>,
  refKey: VariableReference,
) => {
  const id = action.payload.id;
  const variableRefs = action.payload.references?.variables;
  if (variableRefs === undefined) {
    return;
  }
  handleUpdateEntityReferences(
    state.variables.entities,
    id,
    variableRefs,
    variablesReferencesAdapter.addReference,
    removeReferenceAndPrune,
    state.variables,
    refKey,
  );
};

const handleDelete = (
  state: { variables: EntityState<Variable, string> },
  action: PayloadAction<{ id: string }>,
  refKey: VariableReference,
) => {
  handleDeleteEntityReferences(
    state.variables.entities,
    action.payload.id,
    removeReferenceAndPrune,
    state.variables,
    refKey,
  );
};

const slice = createSlice({
  name: 'variables',
  initialState: {
    variables: variablesAdapter.getInitialState(),
  },
  reducers: {},
  extraReducers: builder => {
    builder.addCase(createVariable, (state, action) => {
      const variable: Variable = {
        id: action.payload.id,
        name: action.payload.name,
        references:
          action.payload.references ||
          variablesReferencesAdapter.createInitialState(),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      variablesAdapter.addOne(
        state.variables,
        assignMetadata(action, variable),
      );
    });

    builder.addCase(updateVariable, (state, action) => {
      const { id, ...updates } = action.payload;
      const existing = state.variables.entities[id];
      if (existing) {
        const changes = assignMetadata(
          action,
          assignDefinedFields(
            { ...existing },
            {
              name: updates.name,
              references: updates.references,
            },
          ),
        );
        variablesAdapter.updateOne(state.variables, { id, changes });
      }
    });

    builder.addCase(deleteVariable, (state, action) => {
      variablesAdapter.removeOne(state.variables, action.payload.id);
    });

    // Functions references
    builder.addCase(createFunction, (state, action) =>
      handleCreate(state, action, 'functions'),
    );

    builder.addCase(updateFunction, (state, action) =>
      handleUpdate(state, action, 'functions'),
    );

    builder.addCase(deleteFunction, (state, action) =>
      handleDelete(state, action, 'functions'),
    );

    // Start functions references
    builder.addCase(createStartFunction, (state, action) =>
      handleCreate(state, action, 'startFunctions'),
    );

    builder.addCase(updateStartFunction, (state, action) =>
      handleUpdate(state, action, 'startFunctions'),
    );

    builder.addCase(deleteStartFunction, (state, action) =>
      handleDelete(state, action, 'startFunctions'),
    );

    // End functions references
    builder.addCase(createEndFunction, (state, action) =>
      handleCreate(state, action, 'endFunctions'),
    );

    builder.addCase(updateEndFunction, (state, action) =>
      handleUpdate(state, action, 'endFunctions'),
    );

    builder.addCase(deleteEndFunction, (state, action) =>
      handleDelete(state, action, 'endFunctions'),
    );

    builder.addCase(importFlow, (state, action) => {
      action.payload.globalFunctions.forEach(f => {
        const functionId = f.id;
        const variableRefs = f.references?.variables;
        if (variableRefs === undefined) {
          return;
        }
        handleUpdateEntityReferences(
          state.variables.entities,
          functionId,
          variableRefs,
          variablesReferencesAdapter.addReference,
          removeReferenceAndPrune,
          state.variables,
          'functions',
        );
      });
    });

    // Delay responses references (via updateLatencyControl)
    builder.addCase(updateLatencyControl, (state, action) => {
      const { delayResponses, functionId } = action.payload;
      if (!delayResponses) {
        return;
      }

      const incomingResponses = delayResponses.delayResponses ?? [];
      const presentIds = new Set(incomingResponses.map(d => d.id));

      // Delete omitted references
      const prefix = `${functionId}/`;
      Object.values(state.variables.entities).forEach(variable => {
        if (!variable || !variable.references?.delayResponses) {
          return;
        }
        Object.keys(variable.references.delayResponses).forEach(refKey => {
          const idPart = refKey.slice(prefix.length);

          // No need to prune if it's not from the function or already present
          if (!refKey.startsWith(prefix) || presentIds.has(idPart)) {
            return;
          }

          removeReferenceAndPrune(
            state.variables,
            variable.id,
            'delayResponses',
            refKey,
          );
        });
      });

      // Update references for provided delay responses
      incomingResponses.forEach(delayResponse => {
        const variableRefs = delayResponse.references?.variables;
        // Missing references.variables means no change
        if (variableRefs === undefined) {
          return;
        }
        handleUpdateEntityReferences(
          state.variables.entities,
          `${functionId}/${delayResponse.id}`,
          variableRefs,
          variablesReferencesAdapter.addReference,
          removeReferenceAndPrune,
          state.variables,
          'delayResponses',
        );
      });
    });

    // Topics references
    builder.addCase(createTopic, (state, action) =>
      handleCreate(state, action, 'topics'),
    );

    builder.addCase(updateTopic, (state, action) =>
      handleUpdate(state, action, 'topics'),
    );

    builder.addCase(deleteTopic, (state, action) =>
      handleDelete(state, action, 'topics'),
    );

    builder.addCase(importTopics, (state, action) => {
      action.payload.created.forEach(topic => {
        const topicId = topic.id;
        const variableRefs = topic.references?.variables;
        if (variableRefs === undefined) {
          return;
        }
        handleUpdateEntityReferences(
          state.variables.entities,
          topicId,
          variableRefs,
          variablesReferencesAdapter.addReference,
          removeReferenceAndPrune,
          state.variables,
          'topics',
        );
      });
      action.payload.updated.forEach(topic => {
        const topicId = topic.id;
        const variableRefs = topic.references?.variables;
        if (variableRefs === undefined) {
          return;
        }
        handleUpdateEntityReferences(
          state.variables.entities,
          topicId,
          variableRefs,
          variablesReferencesAdapter.addReference,
          removeReferenceAndPrune,
          state.variables,
          'topics',
        );
      });
    });

    // SMS references
    builder.addCase(createTemplate, (state, action) =>
      handleCreate(state, action, 'sms'),
    );

    builder.addCase(updateTemplate, (state, action) =>
      handleUpdate(state, action, 'sms'),
    );

    builder.addCase(deleteTemplate, (state, action) =>
      handleDelete(state, action, 'sms'),
    );

    // Rules references (behaviours)
    builder.addCase(updateRules, (state, action) => {
      if (!action.payload.references?.variables) {
        return;
      }
      updateSingletonReferences(
        state.variables.entities,
        BEHAVIOUR_REFERENCE_KEY,
        action.payload.references.variables,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'behaviours',
      );
    });

    // greetings references
    builder.addCase(updateGreeting, (state, action) => {
      if (!action.payload.references?.variables) {
        return;
      }
      updateSingletonReferences(
        state.variables.entities,
        GREETING_REFERENCE_KEY,
        action.payload.references.variables,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'greetings',
      );
    });

    // unified channel greeting references
    builder.addCase(updateChannelGreeting, (state, action) => {
      if (!action.payload.greeting?.references?.variables) {
        return;
      }
      const referenceKey = getChannelGreetingReferenceKey(
        action.payload.channelType,
      );
      updateSingletonReferences(
        state.variables.entities,
        referenceKey,
        action.payload.greeting.references.variables,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'greetings',
      );
    });

    // roles references
    builder.addCase(updateRole, (state, action) => {
      if (!action.payload.references?.variables) {
        return;
      }
      updateSingletonReferences(
        state.variables.entities,
        ROLE_REFERENCE_KEY,
        action.payload.references.variables,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'roles',
      );
    });

    // personalities references
    builder.addCase(updatePersonality, (state, action) => {
      if (!action.payload.references?.variables) {
        return;
      }
      updateSingletonReferences(
        state.variables.entities,
        PERSONALITY_REFERENCE_KEY,
        action.payload.references?.variables,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'personalities',
      );
    });

    // Flow step references
    builder.addCase(createFlowStep, (state, action) => {
      const variableRefs = action.payload.step.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.step.id,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowSteps',
      );
    });

    builder.addCase(updateFlowStep, (state, action) => {
      const variableRefs = action.payload.step.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.step.id,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowSteps',
      );
    });

    builder.addCase(deleteFlowStep, (state, action) => {
      handleDeleteEntityReferences(
        state.variables.entities,
        action.payload.stepId,
        removeReferenceAndPrune,
        state.variables,
        'flowSteps',
      );
    });

    // No code step references
    builder.addCase(createNoCodeStep, (state, action) => {
      const variableRefs = action.payload.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.stepId,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowNoCodeSteps',
      );
    });

    builder.addCase(updateNoCodeStep, (state, action) => {
      const variableRefs = action.payload.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.stepId,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowNoCodeSteps',
      );
    });

    builder.addCase(deleteNoCodeStep, (state, action) => {
      handleDeleteEntityReferences(
        state.variables.entities,
        action.payload.stepId,
        removeReferenceAndPrune,
        state.variables,
        'flowNoCodeSteps',
      );
    });

    builder.addCase(deleteStep, (state, action) => {
      handleDeleteEntityReferences(
        state.variables.entities,
        action.payload.stepId,
        removeReferenceAndPrune,
        state.variables,
        'flowNoCodeSteps',
      );
    });

    // Flow function references
    builder.addCase(createFlowTransitionFunction, (state, action) => {
      const variableRefs =
        action.payload.transitionFunction.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.transitionFunction.id,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowFunctions',
      );
    });

    builder.addCase(updateFlowTransitionFunction, (state, action) => {
      const variableRefs =
        action.payload.transitionFunction.references?.variables;
      if (variableRefs === undefined) {
        return;
      }
      handleUpdateEntityReferences(
        state.variables.entities,
        action.payload.transitionFunction.id,
        variableRefs,
        variablesReferencesAdapter.addReference,
        removeReferenceAndPrune,
        state.variables,
        'flowFunctions',
      );
    });

    builder.addCase(deleteFlowTransitionFunction, (state, action) => {
      handleDeleteEntityReferences(
        state.variables.entities,
        action.payload.functionId,
        removeReferenceAndPrune,
        state.variables,
        'flowFunctions',
      );
    });
  },
});

export type { Variable };
export { variablesAdapter };
export { variablesSelectors };

export { selectVariableById, selectVariables } from './selectors';
export { createVariable, deleteVariable, updateVariable };

export default slice.reducer;
